import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, inr, formatDate } from "@/lib/api";
import { PageHeader, StatusChip } from "@/components/Bits";
import UploadOverlay from "@/components/UploadOverlay";
import BulkUploadDialog from "@/components/BulkUploadDialog";
import CreateInvoiceDialog from "@/components/CreateInvoiceDialog";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { UploadSimple, CaretDown, X, Stack, FilePlus } from "@phosphor-icons/react";
import CameraCaptureButton from "@/components/CameraCaptureButton";
import { useAuth } from "@/contexts/AuthContext";

export default function SalesList() {
    const { user } = useAuth();
    const canPost = ["founder", "accountant"].includes(user?.role);
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [uploadName, setUploadName] = useState("");
    const [uploadKind, setUploadKind] = useState("invoice");
    const [filter, setFilter] = useState("all"); // all | invoice | credit_note
    const [pendingReview, setPendingReview] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);
    const nav = useNavigate();

    const load = async () => {
        try {
            const params = {};
            if (filter !== "all") params.doc_type = filter;
            if (pendingReview) params.status = "draft";
            const { data } = await api.get("/sales-invoices", { params });
            setItems(data);
        }
        catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, [filter, pendingReview]);

    const dateFilterActive = !!(dateFrom || dateTo);
    const [sortCol, setSortCol] = useState("invoice_date");
    const [sortDir, setSortDir] = useState("asc");
    const toggleSort = (col) => {
        if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortCol(col); setSortDir(col === "invoice_date" ? "asc" : "desc"); }
    };
    const filtered = useMemo(() => {
        let rows = items;
        if (dateFilterActive) {
            rows = rows.filter((p) => {
                const d = (p.invoice_date || p.created_at || "").slice(0, 10);
                if (dateFrom && d < dateFrom) return false;
                if (dateTo && d > dateTo) return false;
                return true;
            });
        }
        const dir = sortDir === "asc" ? 1 : -1;
        const key = (r) => {
            switch (sortCol) {
                case "invoice_date": return (r.invoice_date || r.created_at || "");
                case "customer":     return (r.customer_name || "").toLowerCase();
                case "invoice_no":   return (r.invoice_no || "").toLowerCase();
                case "subtotal":     return Number(r.subtotal) || 0;
                case "gst_amount":   return Number(r.gst_amount) || 0;
                case "total":        return Number(r.total) || 0;
                case "status":       return r.status || "";
                case "posted_by":    return (r.posted_by_name || r.created_by_name || "").toLowerCase();
                default:             return 0;
            }
        };
        return [...rows].sort((a, b) => {
            const ka = key(a), kb = key(b);
            if (ka < kb) return -1 * dir;
            if (ka > kb) return 1 * dir;
            return 0;
        });
    }, [items, dateFrom, dateTo, dateFilterActive, sortCol, sortDir]);
    const tally = useMemo(() => {
        if (!dateFilterActive) return null;
        const sign = (p) => (p.doc_type === "credit_note" ? -1 : 1);
        const sub = filtered.reduce((s, p) => s + (Number(p.subtotal) || 0) * sign(p), 0);
        const gst = filtered.reduce((s, p) => s + (Number(p.gst_amount) || 0) * sign(p), 0);
        const total = filtered.reduce((s, p) => s + (Number(p.total) || 0) * sign(p), 0);
        return { count: filtered.length, sub, gst, total };
    }, [filtered, dateFilterActive]);

    const upload = async (file, docType = "invoice") => {
        if (!file) return;
        setBusy(true);
        setUploadName(file.name);
        setUploadKind(docType);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post("/sales-invoices/extract", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                params: { doc_type: docType },
            });
            toast.success(docType === "credit_note" ? "Credit note uploaded — AI is reading it" : "Uploaded — AI is reading your invoice");
            nav(`/sales/${data.id}`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); setUploadName(""); }
    };

    return (
        <>
            <Toaster richColors position="top-right" />
            <UploadOverlay open={busy} filename={uploadName} kind={uploadKind === "credit_note" ? "credit note" : "sales"} />
            <PageHeader title="Sales" sub="Sales invoices and credit notes. Revenue is recognised on invoice date; credit notes reduce it."
                right={
                    <div className="flex items-center gap-2 relative">
                        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                               title="From date"
                               className="text-xs border rounded-sm px-2 py-1 bg-white h-8"
                               data-testid="sl-date-from" />
                        <span className="text-xs text-stone-400">to</span>
                        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                               title="To date"
                               className="text-xs border rounded-sm px-2 py-1 bg-white h-8"
                               data-testid="sl-date-to" />
                        {(dateFrom || dateTo) ? (
                            <button type="button" onClick={() => { setDateFrom(""); setDateTo(""); }}
                                    className="text-stone-400 hover:text-stone-700 px-1"
                                    title="Clear dates" data-testid="sl-date-clear">
                                <X size={13} />
                            </button>
                        ) : null}
                        <div className="inline-flex rounded-sm border" style={{ borderColor: "var(--se-border)" }} data-testid="sales-doctype-filter">
                            {[["all", "All"], ["invoice", "Invoices"], ["credit_note", "Credit notes"]].map(([k, label]) => (
                                <button
                                    key={k}
                                    onClick={() => setFilter(k)}
                                    data-testid={`filter-${k}`}
                                    className={`px-3 py-1.5 text-xs ${filter === k ? "text-white" : "text-stone-600"}`}
                                    style={{ background: filter === k ? "var(--se-brand)" : "transparent" }}
                                >{label}</button>
                            ))}
                        </div>
                        {canPost ? (
                            <button
                                onClick={() => setPendingReview((v) => !v)}
                                data-testid="sales-pending-review-btn"
                                className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-sm border ${pendingReview ? "text-white" : "text-amber-800"}`}
                                style={{
                                    background: pendingReview ? "#b45309" : "#fef3c7",
                                    borderColor: pendingReview ? "#b45309" : "#fcd34d",
                                }}
                                title="Team-uploaded sales invoices waiting for you to review and post"
                            >
                                Pending my review
                            </button>
                        ) : null}

                        <CameraCaptureButton
                            onFile={(f) => upload(f, "invoice")}
                            label="Camera"
                            testid="sales-camera-btn"
                            disabled={busy}
                        />
                        <button
                            onClick={() => setCreateOpen(true)}
                            disabled={busy}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs border rounded-sm text-emerald-800 bg-emerald-50 hover:bg-emerald-100 ${busy ? "opacity-50 pointer-events-none" : ""}`}
                            style={{ borderColor: "#a7f3d0" }}
                            title="Create a fresh GST invoice from scratch (no upload)"
                            data-testid="sales-create-invoice-btn"
                        >
                            <FilePlus size={14} /> Create invoice
                        </button>
                        <button
                            onClick={() => setBulkOpen(true)}
                            disabled={busy}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs border rounded-sm text-purple-800 bg-purple-50 hover:bg-purple-100 ${busy ? "opacity-50 pointer-events-none" : ""}`}
                            style={{ borderColor: "#c4b5fd" }}
                            title="Upload many invoices in one go — all pinned to a single B2B/B2C type"
                            data-testid="sales-bulk-upload-btn"
                        >
                            <Stack size={14} /> Bulk upload
                        </button>
                        <div className="relative">
                            <button
                                onClick={() => setMenuOpen((v) => !v)}
                                disabled={busy}
                                className={`inline-flex items-center gap-2 px-4 py-2 text-sm text-white se-btn-primary ${busy ? "opacity-50 pointer-events-none" : ""}`}
                                data-testid="sales-upload-btn"
                            >
                                <UploadSimple size={16} /> {busy ? "Uploading…" : "Upload"} <CaretDown size={12} />
                            </button>
                            {menuOpen ? (
                                <div className="absolute right-0 top-full mt-1 bg-white border rounded-sm shadow-lg z-50 min-w-[180px]" style={{ borderColor: "var(--se-border)" }}>
                                    <label className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 cursor-pointer" data-testid="upload-invoice">
                                        <input type="file" accept="application/pdf,image/*" className="hidden"
                                               onChange={(e) => { setMenuOpen(false); upload(e.target.files?.[0], "invoice"); }} />
                                        Sales invoice
                                    </label>
                                    <label className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 cursor-pointer border-t" style={{ borderColor: "var(--se-border)" }} data-testid="upload-credit-note">
                                        <input type="file" accept="application/pdf,image/*" className="hidden"
                                               onChange={(e) => { setMenuOpen(false); upload(e.target.files?.[0], "credit_note"); }} />
                                        Credit note
                                    </label>
                                </div>
                            ) : null}
                        </div>
                    </div>
                }/>
            <div className="p-8">
                {tally ? (
                    <div className="bg-white border rounded-md px-4 py-3 mb-4 flex items-center gap-6 text-xs" style={{ borderColor: "var(--se-border)" }} data-testid="sl-tally">
                        <div><div className="text-[10px] uppercase tracking-widest text-stone-500">Rows</div><div className="font-mono text-sm mt-0.5">{tally.count}</div></div>
                        <div><div className="text-[10px] uppercase tracking-widest text-stone-500">Taxable</div><div className="font-mono text-sm mt-0.5">₹ {inr(tally.sub)}</div></div>
                        <div><div className="text-[10px] uppercase tracking-widest text-stone-500">GST</div><div className="font-mono text-sm mt-0.5">₹ {inr(tally.gst)}</div></div>
                        <div><div className="text-[10px] uppercase tracking-widest text-stone-500">Total</div><div className="font-mono text-sm mt-0.5 font-semibold">₹ {inr(tally.total)}</div></div>
                        <div className="ml-auto text-[10px] text-stone-500">
                            Credit notes are subtracted. Range: {dateFrom || "start"} → {dateTo || "today"}.
                        </div>
                    </div>
                ) : null}
                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <table className="se-table" data-testid="sales-table">
                        <thead><tr>
                            <SortTh col="invoice_date" label="Date" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} />
                            <th>Type</th>
                            <SortTh col="customer" label="Customer" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} />
                            <SortTh col="invoice_no" label="No." sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} />
                            <SortTh col="subtotal" label="Subtotal" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} num />
                            <SortTh col="gst_amount" label="GST" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} num />
                            <SortTh col="total" label="Total" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} num />
                            <SortTh col="status" label="Status" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} />
                            <SortTh col="posted_by" label="Posted" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} />
                        </tr></thead>
                        <tbody>
                            {filtered.length === 0 ? <tr><td colSpan="9" className="text-stone-500 py-8 text-center">Nothing here yet.</td></tr> :
                                filtered.map((s) => {
                                    const isCN = s.doc_type === "credit_note";
                                    return (
                                        <tr key={s.id} className="cursor-pointer" onClick={() => nav(`/sales/${s.id}`)} data-testid={`row-${s.id}`}>
                                            <td>{formatDate(s.invoice_date || s.created_at)}</td>
                                            <td>
                                                {isCN ? (
                                                    <span className="se-chip se-chip-warn text-[10px]">credit note</span>
                                                ) : (
                                                    <span className="text-xs text-stone-500">invoice</span>
                                                )}
                                            </td>
                                            <td><div className="font-medium">{s.customer_name || "—"}</div><div className="text-xs text-stone-500">{s.is_b2b ? "B2B" : "B2C"}{s.customer_gstin ? ` · ${s.customer_gstin}` : ""}</div></td>
                                            <td>{s.invoice_no || "—"}{s.linked_invoice_no ? <div className="text-[10px] text-stone-400">→ {s.linked_invoice_no}</div> : null}</td>
                                            <td className="se-num">₹ {inr(s.subtotal)}</td>
                                            <td className="se-num">₹ {inr(s.gst_amount)}</td>
                                            <td className="se-num font-medium" style={{ color: isCN ? "#9f1239" : undefined }}>
                                                {isCN ? "-" : ""}₹ {inr(s.total)}
                                            </td>
                                            <td><StatusChip status={s.status} /></td>
                                            <td className="text-[11px]">
                                                {s.status === "posted" && s.posted_by_name ? (
                                                    <>
                                                        <div>{s.posted_by_name}</div>
                                                        <div className="text-[10px] text-stone-400">{formatDate(s.posted_at)}</div>
                                                    </>
                                                ) : s.status === "draft" && s.created_by_name ? (
                                                    <>
                                                        <div className="text-amber-700 italic">uploaded by</div>
                                                        <div className="text-stone-500">{s.created_by_name}</div>
                                                    </>
                                                ) : (
                                                    <span className="text-stone-400">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            }
                        </tbody>
                    </table>
                </div>
            </div>
            <BulkUploadDialog open={bulkOpen} onClose={() => setBulkOpen(false)}
                                kind="sales" onAllDone={load} />
            <CreateInvoiceDialog open={createOpen} onClose={() => setCreateOpen(false)}
                                 onDone={load} />
        </>
    );
}


function SortTh({ col, label, sortCol, sortDir, onClick, num }) {
    const active = sortCol === col;
    return (
        <th className={num ? "text-right" : ""}>
            <button
                onClick={() => onClick(col)}
                className={`inline-flex items-center gap-1 select-none hover:text-purple-700 transition-colors ${num ? "flex-row-reverse" : ""}`}
                style={{ color: active ? "#7c3aed" : "inherit", fontWeight: active ? 600 : "inherit" }}
                data-testid={`sort-${col}`}
            >
                {label}
                <span className="text-[9px] opacity-60">
                    {active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
                </span>
            </button>
        </th>
    );
}

